import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import { Link } from "react-router-dom";
import ReactDOM from "react-dom";
import SearchFull from "./SearchFull";
import { motion } from "framer-motion";
import {
  fetchSearchResults,
  setSearchParams,
} from "../store/slices/searchSlice";

const libraries = ["places"];
const SkeletonLoader = () => (
  <div className="skeleton-loader">
    <div className="skeleton-logo"></div>
    <div className="skeleton-search"></div>
    <div className="skeleton-cta"></div>
  </div>
);
const TopHeaderFull = ({ compact = false }) => {
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [isFullSearchVisible, setIsFullSearchVisible] = useState(false);
  const searchRef = useRef(null);
  const searchContainerRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation(); // Use useLocation to access location

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsFullSearchVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchSubmit = (searchData) => {
    // console.log("TopHeaderFull: handleSearchSubmit called with:", searchData); // Log the data

    if (location.pathname === "/resultats") {
      // If already on results page, the SearchFull component
      // will handle updating the results. No navigation needed.
    } else {
      navigate("/resultats", { state: { searchData } });
    }
    setIsFullSearchVisible(false); // Close the full search
  };

  const handleCompactClick = (e) => {
    e.preventDefault();
    setIsFullSearchVisible(true);
  };

  if (!isLoaded) {
    return <SkeletonLoader />;
  }

  // Single instance of SearchFull
  const searchFullComponent = (
    <SearchFull
      onSubmit={handleSearchSubmit}
      compact={!isFullSearchVisible}
      isGoogleLoaded={isLoaded}
      category={false}
    />
  );

  return (
    <div className="twyny-border-bottom py-0">
      <div className="topheaderfull" ref={searchRef}>
        <header className=" container d-flex justify-content-between align-items-center topheaderfull-topbar">
          <div className="topheaderfull-logo">
            <Link to="/">
              <img
                src="/images/twyLogo.svg"
                alt="Twyny Logo"
                className="logo-img"
              />
            </Link>
          </div>

          {/* Initially inside topheaderfull */}
          {!isFullSearchVisible && (
            <div
              className="topheaderfull-search d-lg-block "
              onClick={handleCompactClick}
            >
              {searchFullComponent}
            </div>
          )}

          <div className="topheaderfull-cta">
            {isAuthenticated ? (
              <Link
                to="/compte/informations-personnel"
                className="user-profile-link"
              >
                <img
                  src="/images/user-profile.svg"
                  alt="user-profile"
                  className="user-profile-img"
                />
                 {user?.prenom}  {user?.nom}
              </Link>
            ) : (
              <>
                <Link
                  to="/registerPrestataire"
                  className="topheaderfull-cta-item mr-8"
                >
                  <button className="btn twyny-cta-secondary d-lg-block d-md-none d-none">
                    Devenir partenaire
                  </button>
                  <button className="btn twyny-cta-secondary d-lg-none d-md-block d-block">
                    Pro
                  </button>
                </Link>
                <Link to="/login" className="topheaderfull-cta-item ml-8">
                  <button className="btn twyny-cta-primary d-lg-block d-md-none d-none">
                    Se connecter
                  </button>
                  <button className="btn twyny-cta-primary d-lg-none d-md-block d-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 27 30"
                    >
                      <g
                        id="Icon_feather-user"
                        data-name="Icon feather-user"
                        transform="translate(-4.5 -3)"
                      >
                        <path
                          id="Tracé_8327"
                          data-name="Tracé_8327"
                          d="M30,31.5v-3a6,6,0,0,0-6-6H12a6,6,0,0,0-6,6v3"
                          fill="none"
                          stroke="#fff"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                        />
                        <path
                          id="Tracé_8328"
                          data-name="Tracé_8328"
                          d="M24,10.5a6,6,0,1,1-6-6A6,6,0,0,1,24,10.5Z"
                          fill="none"
                          stroke="#fff"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                        />
                      </g>
                    </svg>
                  </button>
                </Link>
              </>
            )}
          </div>
        </header>
      </div>

      {/* Moves to full-search-container when active */}
      <motion.div
        className="overlay "
        initial={{ opacity: 0.5 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
      />
      <motion.div
        ref={searchContainerRef}
        className="full-search-container pb-19"
        exit={{ scale: 0.8, opacity: 0 }}
        initial={{ y: -100, scale: 0.8, opacity: 0 }}
        animate={{
          y: isFullSearchVisible ? 0 : -100,
          scale: isFullSearchVisible ? 1 : 0.8,
          opacity: isFullSearchVisible ? 1 : 0,
        }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        {isFullSearchVisible &&
          ReactDOM.createPortal(
            searchFullComponent,
            searchContainerRef.current
          )}
      </motion.div>
    </div>
  );
};

export default TopHeaderFull;
